import React, { useState } from 'react';
import { Card, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { Search, Flag, CreditCard, Ticket, Briefcase, GraduationCap } from 'lucide-react';

const KeywordSelector = ({ onKeywordChange, currentKeyword = 'Singapore PR', currentCountry = 'SG' }) => {
  const [customKeyword, setCustomKeyword] = useState('');
  const [country, setCountry] = useState(currentCountry);

  const quickKeywords = [
    { name: 'Singapore PR', icon: CreditCard, color: 'outline-primary' },
    { name: 'Singapore Citizenship', icon: Flag, color: 'outline-success' },
    { name: 'Singapore LTVP', icon: Ticket, color: 'outline-info' },
    { name: 'Employment Pass', icon: Briefcase, color: 'outline-warning' },
    { name: 'Student Pass Singapore', icon: GraduationCap, color: 'outline-secondary' },
    { name: 'Immigration consultant', icon: Briefcase, color: 'outline-primary' },
    { name: 'Immigration agency', icon: Briefcase, color: 'outline-success' },
    { name: 'ONE Pass', icon: Briefcase, color: 'outline-info' }
  ];

  const countries = [
    { value: 'SG', label: 'Singapore' },
    { value: 'US', label: 'United States' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'CA', label: 'Canada' },
    { value: 'AU', label: 'Australia' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'HK', label: 'Hong Kong' },
    { value: 'JP', label: 'Japan' },
    { value: '', label: 'Global (All Regions)' }
  ];

  const handleQuickSelect = (keyword) => {
    onKeywordChange(keyword, country);
  };

  const handleCustomSearch = (e) => {
    e.preventDefault();
    if (customKeyword.trim()) {
      onKeywordChange(customKeyword.trim(), country);
    }
  };

  const handleCountryChange = (newCountry) => {
    setCountry(newCountry);
    onKeywordChange(currentKeyword, newCountry);
  };

  const getCountryLabel = (value) => {
    const country = countries.find(c => c.value === value);
    return country ? country.label : 'Singapore';
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <h5 className="card-title mb-0">
          <Search className="text-primary me-2" size={20} />
          Search Trends Selection
        </h5>
      </Card.Header>
      <Card.Body>
        {/* Quick Select Buttons */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">Quick Select - Singapore Immigration Topics:</h6>
          <div className="d-flex flex-wrap gap-2">
            {quickKeywords.map((keyword) => {
              const Icon = keyword.icon;
              return (
                <Button
                  key={keyword.name}
                  variant={keyword.color}
                  onClick={() => handleQuickSelect(keyword.name)}
                  className="d-flex align-items-center"
                >
                  <Icon size={16} className="me-2" />
                  {keyword.name}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Custom Search */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">Custom Search:</h6>
          <Form onSubmit={handleCustomSearch}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Keyword(s)</Form.Label>
                <Form.Control
                  type="text"
                  value={customKeyword}
                  onChange={(e) => setCustomKeyword(e.target.value)}
                  placeholder="e.g., Singapore work visa, digital nomad pass"
                />
                <Form.Text className="text-muted">
                  Enter search terms separated by commas for multiple keywords
                </Form.Text>
              </Col>
              <Col md={4}>
                <Form.Label>Country/Region</Form.Label>
                <Form.Select
                  value={country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {countries.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select geographic region for trends
                </Form.Text>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <Button type="submit" variant="primary" className="w-100">
                  <Search size={16} className="me-2" />
                  Search
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        {/* Current Selection Display */}
        <Alert variant="info" className="d-flex align-items-center">
          <Search size={20} className="me-3 flex-shrink-0" />
          <div className="flex-grow-1">
            <strong>Currently searching for:</strong>
            <span className="badge bg-primary ms-2">{currentKeyword}</span>
            <span className="badge bg-secondary ms-2">{getCountryLabel(country)}</span>
          </div>
        </Alert>
      </Card.Body>
    </Card>
  );
};

export default KeywordSelector;